import React, { useEffect, useMemo, useState } from 'react';
import { Container, Box } from '@mui/material';
import StyledBox from 'views/membership-module/MembershipView/MembershipView.styles';
import { MembershipViewSkeleton } from 'views/membership-module/MembershipView/MembershipViewSkeleton';
import Head from 'next/head';

import { useTranslation } from 'next-i18next';
import {
  HeroImgSection,
  HeaderSection,
  WhyChooseSubscriptionSection,
  PlanSection,
} from 'views/membership-module/MembershipView/components';
import {
  MapGroupWrapper,
  MotorcycleRentals,
  Typography700,
  MembershipFAQ,
} from 'views/common/components';
import { HowItWorksSection } from 'views/membership-module/MembershipLandingView/components';

import axios, { API } from 'lib/config/axios';

import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from 'redux/features/ui-slice';

import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import PATHS from 'lib/navigation/paths';
import { SECTION_ID } from 'views/membership-module/MembershipLandingView/utils/constants';
import generateMetaData from 'lib/utils/generateMetaData';

const MembershipView = (props) => {
  const { serverData } = props;

  const { t } = useTranslation('fe_er_membership_page');
  const handleErrorResponse = useErrorsHandler();
  const dispatch = useDispatch();
  const appState = useSelector((state) => state.globalData.appState);
  const appNavigator = useAppNavigator();
  const [data, setData] = useState(serverData);

  const unlockHeader = useMemo(
    () => ({
      title: t('fe_er_membership_page:unlock_versatility'),
      titleHint: t('fe_er_membership_page:switch_bikes_every_week'),
      subTitle: t('fe_er_membership_page:ride_top_brand_bikes'),
      orangeTitle: true,
    }),
    [],
  );
  const whyChooseHeader = useMemo(
    () => ({
      title: t('fe_er_membership_page:why_choose'),
      titleHint: t('fe_er_membership_page:motorcycle_subscription'),
      subTitle: t('fe_er_membership_page:experience_open_road'),
      orangeHint: true,
    }),
    [],
  );

  const getData = () => {
    if (data) {
      dispatch(setLoading(true));
    }
    axios
      .get(API.membership.slug.replace('{slug}', appNavigator.query.slug))
      .then((res) => {
        setData(res.data);
      })
      .catch((error) => {
        switch (error.response?.status) {
          case 302:
            appNavigator.push(
              PATHS.motorcycleClub.slug(error.response.data.new_slug),
            );
            break;
          default:
            handleErrorResponse(error, false);
            break;
        }
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const vehicleMakes = useMemo(() => {
    const vms = [];
    if (data?.available_vehicle_models) {
      data.available_vehicle_models.forEach((vehicleModel) => {
        const existed = vms.find(
          (vm) => vm.id === vehicleModel.vehicle_make.id,
        );
        if (!existed) {
          vms.push(vehicleModel.vehicle_make);
        }
      });
    }
    return vms;
  }, [data?.available_vehicle_models]);

  const getLocationVehicleModelsAndCalendars = (params) => {
    dispatch(setLoading(true));
    return axios
      .get(API.membership.LocationVehicleModelsAndCalendars, { params })
      .then((res) => {
        setData({
          ...data,
          selected_location: res.data.selected_location,
          available_vehicle_models: res.data.available_vehicle_models,
          blackout_calendars: res.data.blackout_calendars,
        });
      })
      .catch((error) => {
        handleErrorResponse(error);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handleLocationChange = (e) => {
    const params = {
      location_id: e.id,
    };
    return getLocationVehicleModelsAndCalendars(params);
  };

  const getSubscribeNowPath = () => {
    if (appState.current_user) {
      return PATHS.motorcycleClub.checkout({
        membership_id: data.memberships[0].id,
      });
    }
    return PATHS.motorcycleClub.signUp({
      membership_id: data.memberships[0].id,
    });
  };

  useEffect(() => {
    if (!serverData) {
      getData();
    }
  }, [appNavigator.query.slug]);

  return (
    <>
      {data ? (
        <StyledBox className="overFlowXHidden">
          <Head>{data?.seo_values && generateMetaData(data.seo_values)}</Head>
          <HeroImgSection
            membershipData={data.memberships[0]}
            getSubscribeNowPath={getSubscribeNowPath}
          />
          <Container maxWidth="xl" className="RTVContainer">
            <Box className="RTVSectionSpacing">
              <HeaderSection
                {...unlockHeader}
                getSubscribeNowPath={getSubscribeNowPath}
              />
              <Box mt={3}>
                <MotorcycleRentals
                  selectedLocation={data.selected_location}
                  participatingLocations={data.participating_locations}
                  vehicleModels={data.available_vehicle_models}
                  vehicleMakes={vehicleMakes}
                  handleLocationChange={handleLocationChange}
                />
              </Box>
            </Box>
            <Box className="RTVSectionSpacing">
              <HeaderSection
                {...whyChooseHeader}
                getSubscribeNowPath={getSubscribeNowPath}
              />
              <WhyChooseSubscriptionSection />
            </Box>
            <Box className="RTVSectionSpacing" id={SECTION_ID.COMPARE_PLAN}>
              <PlanSection
                membershipData={data.memberships[0]}
                getSubscribeNowPath={getSubscribeNowPath}
              />
            </Box>
            <Box className="RTVSectionSpacing">
              <Typography700
                className="RTVSectionTitle"
                component="p"
                dangerouslySetInnerHTML={{
                  __html: t(
                    'fe_er_membership_page:how_our_motorcycle_subscription_works',
                  ),
                }}
              />
              <HowItWorksSection
                id={SECTION_ID.HOW_IT_WORKS}
                darkMode
                membershipName={t('fe_er_membership_page:join_club_ride_name', {
                  value: data.memberships[0].name,
                })}
              />
            </Box>
            <Box className="RTVSectionSpacing">
              <Typography700
                className="RTVSectionTitle"
                component="p"
                dangerouslySetInnerHTML={{
                  __html: t('fe_er_membership_page:locations_worldwide', {
                    count: 100,
                  }),
                }}
              />
              <MapGroupWrapper
                mapGroupData={data.geography_component}
                placeholder={t('fe_er_membership_page:search')}
                darkMode
              />
            </Box>
            <Box className="RTVSectionSpacing">
              <Typography700
                className="RTVSectionTitle"
                component="p"
                dangerouslySetInnerHTML={{
                  __html: t('fe_er_membership_page:frequently_asked_questions'),
                }}
              />
              <MembershipFAQ darkMode faqs={data.faq_items} />
            </Box>
          </Container>
        </StyledBox>
      ) : (
        <MembershipViewSkeleton />
      )}
    </>
  );
};

export { MembershipView };
